import { useState, useEffect, useRef } from 'react';
import { ArrowLeft, ArrowRight, Fingerprint } from 'lucide-react';
import { startAuthentication } from '@simplewebauthn/browser';

export default function LoginScreen({ attemptLogin, onPasskeyLogin }) {
  const [profiles, setProfiles] = useState(null);
  const [selected, setSelected] = useState(null);
  const [pw, setPw] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [hasPasskey, setHasPasskey] = useState(false);
  const [pkBusy, setPkBusy] = useState(false);
  const [showPw, setShowPw] = useState(false); // password fallback hidden when a passkey exists
  const pwRef = useRef();

  // does the selected profile have a passkey?
  useEffect(() => {
    setShowPw(false);
    if (!selected) { setHasPasskey(false); return; }
    fetch(`/webauthn/has?user=${encodeURIComponent(selected.id)}`)
      .then(r => r.json()).then(d => setHasPasskey(!!d.has)).catch(() => setHasPasskey(false));
  }, [selected]);

  // passkey-only mode: hide the password input (bot-resistant) until user opts for password
  const passkeyOnly = hasPasskey && !showPw;

  const passkeyLogin = async () => {
    setPkBusy(true); setErr('');
    try {
      const oR = await fetch('/webauthn/login/options', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ user: selected.id }),
      });
      if (!oR.ok) throw new Error('No passkey registered');
      const optionsJSON = await oR.json();
      const response = await startAuthentication({ optionsJSON });
      const vR = await fetch('/webauthn/login/verify', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ user: selected.id, response }),
      });
      const j = await vR.json();
      if (j.ok && j.token) onPasskeyLogin(j.token, j.user);
      else setErr('Passkey verification failed');
    } catch (e) { setErr(e.message === 'No passkey registered' ? e.message : 'Passkey cancelled'); }
    finally { setPkBusy(false); }
  };

  useEffect(() => {
    fetch('/profiles').then(r => r.json()).then(d => {
      const list = d.profiles || [];
      setProfiles(list);
      // preselect last-used profile (still requires password)
      try {
        const last = localStorage.getItem('lastProfile');
        const match = last && list.find(p => p.id === last);
        if (match) { setSelected(match); setErr(''); setPw(''); }
      } catch { /* ignore */ }
    }).catch(() => setProfiles([]));
  }, []);
  useEffect(() => { if (selected) setTimeout(() => pwRef.current?.focus(), 50); }, [selected]);

  const pick = p => { setSelected(p); setErr(''); setPw(''); };
  const back = () => { setSelected(null); setErr(''); setPw(''); };

  const submit = async e => {
    e.preventDefault();
    if (!pw.trim()) { setErr('Enter password'); return; }
    setBusy(true); setErr('');
    const msg = await attemptLogin(pw, selected.id);
    if (msg) { setErr(msg); setBusy(false); setPw(''); pwRef.current?.focus(); }
    // on success the app unmounts this screen
  };

  const avatar = (p, size) => (
    <div className="rounded-full flex items-center justify-center overflow-hidden"
      style={{ width: size, height: size, background: p.avatar ? '#222' : 'var(--accent-grad)', border: '3px solid rgba(255,255,255,0.18)', boxShadow: '0 8px 30px rgba(0,0,0,0.5)' }}>
      {p.avatar ? <img src={p.avatar} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : <span style={{ color: '#fff', fontWeight: 700, fontSize: size * 0.42, fontFamily: "'Space Grotesk',sans-serif" }}>{p.name[0]}</span>}
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col items-center justify-center"
      style={{ background: 'radial-gradient(ellipse at 70% 12%, var(--accent-glow, rgba(180,70,10,0.25)) 0%, #141414 55%, #0a0a0d 100%)' }}>
      <h1 className="font-display mb-12 text-center" style={{ color: '#f5f0e8', fontSize: '26px', fontWeight: 700 }}>Investment Plan</h1>

      {!selected ? (
        <div className="flex items-start justify-center gap-10 flex-wrap px-6">
          {(profiles || []).map(p => (
            <button key={p.id} onClick={() => pick(p)} className="flex flex-col items-center gap-3 transition-transform duration-150 hover:scale-105">
              {avatar(p, 104)}
              <span style={{ color: '#f5f0e8', fontSize: '17px', fontWeight: 500 }}>{p.name}</span>
            </button>
          ))}
          {profiles && profiles.length === 0 && <p style={{ color: 'rgba(255,255,255,0.5)' }}>No profiles configured.</p>}
        </div>
      ) : (
        <form onSubmit={submit} className="flex flex-col items-center gap-4">
          {avatar(selected, 104)}
          <span style={{ color: '#f5f0e8', fontSize: '18px', fontWeight: 600 }}>{selected.name}</span>

          {passkeyOnly ? (
            <>
              <button type="button" onClick={passkeyLogin} disabled={pkBusy}
                className="flex items-center gap-2 mt-1 px-6 py-3 rounded-full text-base font-semibold transition-all active:scale-95"
                style={{ background: 'var(--accent-grad)', color: '#fff', boxShadow: '0 4px 16px var(--accent-glow)', opacity: pkBusy ? 0.6 : 1 }}>
                <Fingerprint size={19} /> {pkBusy ? 'Waiting…' : 'Sign in with passkey'}
              </button>
              {err && <p style={{ color: '#f87171', fontSize: '13px' }}>{err}</p>}
              <button type="button" onClick={() => { setShowPw(true); setErr(''); setTimeout(() => pwRef.current?.focus(), 50); }}
                className="mt-1" style={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px' }}>
                Use password instead
              </button>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 mt-1">
                <input ref={pwRef} type="password" value={pw}
                  onChange={e => { setPw(e.target.value); setErr(''); }}
                  placeholder="Enter Password"
                  className="px-4 py-2.5 rounded-full text-sm outline-none text-center"
                  style={{ background: 'rgba(255,255,255,0.1)', border: `1px solid ${err ? '#f87171' : 'rgba(255,255,255,0.18)'}`, color: '#f5f0e8', width: '220px' }} />
                <button type="submit" disabled={busy} className="rounded-full flex items-center justify-center transition-all active:scale-90"
                  style={{ width: 40, height: 40, background: 'var(--accent-grad)', color: '#fff', flexShrink: 0, opacity: busy ? 0.6 : 1 }}>
                  <ArrowRight size={18} />
                </button>
              </div>
              {err && <p style={{ color: '#f87171', fontSize: '13px' }}>{err}</p>}
              {hasPasskey && (
                <button type="button" onClick={passkeyLogin} disabled={pkBusy}
                  className="flex items-center gap-2 mt-1 px-5 py-2.5 rounded-full text-sm font-semibold transition-all active:scale-95"
                  style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: '#f5f0e8', opacity: pkBusy ? 0.6 : 1 }}>
                  <Fingerprint size={17} /> {pkBusy ? 'Waiting…' : 'Sign in with passkey'}
                </button>
              )}
            </>
          )}
          <button type="button" onClick={back} className="flex items-center gap-1 mt-2" style={{ color: 'rgba(255,255,255,0.45)', fontSize: '14px' }}>
            <ArrowLeft size={14} /> Back
          </button>
        </form>
      )}
    </div>
  );
}
